/**
 * Strict YouTube URL parsing for admin notebook video fields.
 * Returns normalized short URL (youtu.be) + canonical video id; rejects all non-YouTube hosts.
 */

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/

function normalizeHost (hostname: string): string {
  return hostname.replace(/^www\./i, '').toLowerCase()
}

export function isValidYouTubeVideoId (id: string): boolean {
  return YOUTUBE_ID_RE.test(id)
}

/**
 * Parse a user-pasted YouTube URL into `{ videoId, sourceUrl }` or `null` if invalid / not YouTube.
 */
export function parseYouTubeVideoFromUrl (raw: string): { videoId: string, sourceUrl: string } | null {
  const s = raw.trim()
  if (!s) {
    return null
  }

  let u: URL
  try {
    u = new URL(s)
  } catch {
    return null
  }

  const host = normalizeHost(u.hostname)

  if (host === 'youtu.be') {
    const id = u.pathname.replace(/^\//, '').split('/').filter(Boolean)[0] ?? ''
    if (!isValidYouTubeVideoId(id)) {
      return null
    }
    return { videoId: id, sourceUrl: `https://youtu.be/${id}` }
  }

  const isYoutube =
    host === 'youtube.com'
    || host === 'm.youtube.com'
    || host === 'youtube-nocookie.com'

  if (!isYoutube) {
    return null
  }

  if (u.pathname === '/watch' || u.pathname.startsWith('/watch')) {
    const v = u.searchParams.get('v')
    if (!v || !isValidYouTubeVideoId(v)) {
      return null
    }
    return { videoId: v, sourceUrl: `https://youtu.be/${v}` }
  }

  if (u.pathname.startsWith('/embed/')) {
    const id = u.pathname.slice('/embed/'.length).split('/').filter(Boolean)[0] ?? ''
    if (!isValidYouTubeVideoId(id)) {
      return null
    }
    return { videoId: id, sourceUrl: `https://youtu.be/${id}` }
  }

  if (u.pathname.startsWith('/shorts/')) {
    const id = u.pathname.slice('/shorts/'.length).split('/').filter(Boolean)[0] ?? ''
    if (!isValidYouTubeVideoId(id)) {
      return null
    }
    return { videoId: id, sourceUrl: `https://youtu.be/${id}` }
  }

  if (u.pathname.startsWith('/v/')) {
    const id = u.pathname.slice('/v/'.length).split('/').filter(Boolean)[0] ?? ''
    if (!isValidYouTubeVideoId(id)) {
      return null
    }
    return { videoId: id, sourceUrl: `https://youtu.be/${id}` }
  }

  return null
}

/** Safe embed URL for iframes (privacy-enhanced host). */
export function youtubeNocookieEmbedSrc (videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`
}
